/**
 * Brand palette layer for the theme builder.
 *
 * A "palette" is a compact seed that binds a few brand-tunable semantic ROLES
 * to color scales; the theme builder (themes.js) derives every theme token from
 * the resolved base scales. Only the primary/secondary accents and the
 * neutral "surface" ramp are brand-tunable — semantic families (success/error/
 * warning) stay locked across all brands so meaning never rebrands.
 *
 * The DEFAULT_PALETTE reproduces the historical hardcoded choices (primary =
 * blue, secondary = orange, surface = gray) BY REFERENCE, so the default-brand
 * themes stay byte-identical (and identity-equal) to the pre-palette build.
 *
 * Pure data + pure functions only (matches @learnzapp/core's platform-agnostic
 * contract).
 */
import { BASE_COLORS_LIGHT, BASE_COLORS_DARK } from './baseColors';

/**
 * Canonical LearnZapp palette. A role is either:
 *  - { family: '<baseColorFamily>' }  → bind the role to an existing base family
 *  - { light: <scale>, dark: <scale> } → supply custom inline scales (50..950 + DEFAULT)
 *
 * `dataAccents` (optional) is MERGED per-feature over the default home-menu hue
 *               map (a brand restates only the tiles it retunes).
 * `overrides`   (optional) deep-merges literal token values onto the assembled
 *               theme objects, keyed { web|mobile: { light|dark: {...} } }.
 */
export const DEFAULT_PALETTE = {
  accentPrimary: { family: 'blue' },
  accentSecondary: { family: 'orange' },
  surface: { family: 'gray' },
  dataAccents: null,
  overrides: null,
};

// Bind one base-family slot from a role spec. A {family} that already matches
// the slot is a no-op (preserves identity for the default palette); a different
// family swaps the slot; inline {light,dark} scales replace it outright.
function roleOverride(slot, spec, lightOv, darkOv) {
  if (!spec) return;
  if (spec.family) {
    if (spec.family === slot) return;
    lightOv[slot] = BASE_COLORS_LIGHT[spec.family];
    darkOv[slot] = BASE_COLORS_DARK[spec.family];
    return;
  }
  if (spec.light) lightOv[slot] = spec.light;
  if (spec.dark) darkOv[slot] = spec.dark;
}

/**
 * Resolve a brand palette into the { L, D } base-scale objects the theme
 * builder consumes. The default palette yields the canonical base objects BY
 * REFERENCE (identity preserved); a brand palette returns shallow clones with
 * the primary→blue, secondary→orange, surface→gray slots swapped.
 */
export function resolveBaseForPalette(palette) {
  const p = palette || DEFAULT_PALETTE;
  const lightOv = {};
  const darkOv = {};
  roleOverride('blue', p.accentPrimary, lightOv, darkOv);
  roleOverride('orange', p.accentSecondary, lightOv, darkOv);
  roleOverride('gray', p.surface, lightOv, darkOv);
  const L = Object.keys(lightOv).length ? { ...BASE_COLORS_LIGHT, ...lightOv } : BASE_COLORS_LIGHT;
  const D = Object.keys(darkOv).length ? { ...BASE_COLORS_DARK, ...darkOv } : BASE_COLORS_DARK;
  return { L, D };
}

function isPlainObject(v) {
  return v && typeof v === 'object' && !Array.isArray(v);
}

// Pure recursive merge: returns a NEW object; arrays/primitives from `ov`
// overwrite. Never mutates `base` or its nested objects — theme group objects
// are shared by reference across the four assembled theme objects, so an
// in-place merge would corrupt siblings.
function deepMerge(base, ov) {
  const out = { ...base };
  for (const key of Object.keys(ov)) {
    const a = base[key];
    const b = ov[key];
    out[key] = isPlainObject(a) && isPlainObject(b) ? deepMerge(a, b) : b;
  }
  return out;
}

/** Apply optional literal token overrides; returns the input unchanged when none. */
export function applyOverrides(theme, ov) {
  return ov ? deepMerge(theme, ov) : theme;
}
